import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/useLocale';
import { useSeoMeta, useSocialMeta } from '@/hooks/usePageTitle';
import { useLandingPageView } from '@/lib/landingAnalytics';
import { LandingNavigation } from '@/components/landing/LandingNavigation';
import { CalculatorLink, HowItWorksLink } from '@/components/landing/LandingActions';
import { ProductPreview } from '@/components/landing/ProductPreview';
import { Capabilities, TrustSection, AudienceSection } from '@/components/landing/PlanningSections';
import { ScenarioPreview } from '@/components/landing/ScenarioPreview';
import { HowItWorks, MethodologySection, FinalCta, LandingFooter } from '@/components/landing/ClosingSections';
import '@/components/landing/landing.css';

export default function LandingPage() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  useSeoMeta(t('homepage.meta.title'), t('homepage.meta.description'));
  useSocialMeta({ title: t('homepage.meta.title'), description: t('homepage.meta.description') });
  useLandingPageView(lang);
  useEffect(() => {
    // Reuse the SEO owner's canonical; never build a second URL/domain source.
    const canonical = document.querySelector('link[rel="canonical"]')?.href;
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (canonical && ogUrl) ogUrl.setAttribute('content', canonical);
  }, [lang]);

  return <div className="landing-page" data-testid="landing-page">
    <LandingNavigation />
    <main id="landing-main" tabIndex={-1} data-testid="landing-main">
      <section className="lp-hero" data-testid="hero-section" aria-labelledby="hero-heading">
        <div className="lp-container">
          <p className="lp-eyebrow" data-testid="landing-hero-eyebrow">{t('homepage.eyebrow')}</p>
          <h1 id="hero-heading" data-testid="hero-heading">{t('homepage.headline')}</h1>
          <p className="lp-hero-copy" data-testid="landing-hero-description">{t('homepage.intro')}</p>
          <div className="lp-hero-actions">
            <CalculatorLink placement="hero" />
            <ul className="lp-trust-line" data-testid="landing-hero-trust" x-excluded="true">
              {[['noSignup', 'no-signup'], ['transparent', 'transparent'], ['noGate', 'no-gate']].map(([key, id]) => <li key={key} data-testid={`landing-hero-trust-${id}`}>{t(`homepage.${key}`)}</li>)}
            </ul>
            <HowItWorksLink placement="hero" />
          </div>
        </div>
      </section>
      <ProductPreview />
      <Capabilities />
      <TrustSection />
      <ScenarioPreview />
      <AudienceSection />
      <HowItWorks />
      <MethodologySection />
      <FinalCta />
    </main>
    <LandingFooter />
  </div>;
}